import React, { useEffect, useRef } from 'react';
import { Form, Select, Typography, theme } from 'antd';
import { ArrowRightOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';
import { useTranslation } from 'react-i18next';
import { normalizeTransportAddressList } from '../utils/transportAddressUtils';
import { resolveOrderDeliveryAddress } from '../utils/orderDeliveryAddress';
import './TransportOrderRoutePointsSection.css';

interface OrderRouteOption {
  id: string;
  orderNumber: string;
  displayOrderNumber?: string;
  customerName?: string;
  factoryLocation?: string | null;
  deliveryLocation?: string | null;
  customer?: { address?: string | null } | null;
}

export default function TransportOrderRoutePointsSection({ form, orders }: { form: FormInstance; orders: OrderRouteOption[] }) {
  const { t } = useTranslation('transport');
  const { token } = theme.useToken();
  const loading = normalizeTransportAddressList(Form.useWatch('loadingAddresses', { form, preserve: true }) ?? form.getFieldValue('loadingAddresses'));
  const delivery = normalizeTransportAddressList(Form.useWatch('deliveryAddresses', { form, preserve: true }) ?? form.getFieldValue('deliveryAddresses'));
  const signature = JSON.stringify({ loading, delivery, orders });
  // Values this effect filled in itself (not the operator, not a saved truck):
  // they stay replaceable, so a second ZK added before its address reaches the
  // route list is not left on the first ZK's point (review 2026-10-06).
  const autoFilled = useRef(new Map<string, string | null>());
  useEffect(() => {
    const source = JSON.parse(signature) as { loading: string[]; delivery: string[]; orders: OrderRouteOption[] };
    for (const order of source.orders) {
      for (const [field, addresses, hint] of [
        ['loadingAddress', source.loading, order.factoryLocation],
        ['deliveryAddress', source.delivery, resolveOrderDeliveryAddress(order)],
      ] as const) {
        // An empty list means the form's addresses are not seeded yet (or were
        // cleared by a reset), not that the point is gone: leave the saved choice.
        if (addresses.length === 0) continue;
        const path = ['orderRoutePointsByOrder', order.id, field];
        const key = path.join('|');
        const current = form.getFieldValue(path);
        const isAuto = autoFilled.current.has(key) && autoFilled.current.get(key) === (current ?? null);
        if (current && addresses.includes(current) && !isAuto) continue;
        // The order's own place when it is on the route (a ZK on a factory truck: where
        // the ZD of its paired line loads); else a single point; else an earlier automatic
        // choice still on the list; else the operator picks — the save bar names it.
        const own = hint && addresses.includes(hint.trim()) ? hint.trim() : null;
        const kept = isAuto && current && addresses.includes(current) ? current : null;
        const next = own ?? (addresses.length === 1 ? addresses[0] : kept);
        autoFilled.current.set(key, next);
        if (next !== current) form.setFieldValue(path, next);
      }
    }
  }, [form, signature]);
  if (!orders.length) return null;
  // Owner 2026-10-10: one row per order (ZK · A → B), like the journey above.
  return <section className="transport-order-points" style={{
    '--route-point-line': token.colorBorderSecondary,
    '--route-point-surface': token.colorFillAlter,
    '--route-point-accent': token.colorPrimary,
  } as React.CSSProperties}>
    <div className="transport-order-points__title">{t('orderRoutePoints.title')}</div>
    <Typography.Text type="secondary" className="transport-order-points__hint">{t('orderRoutePoints.hint')}</Typography.Text>
    {orders.map(order => <div key={order.id} className="transport-order-points__order">
      <div className="transport-order-points__identity">
        <Typography.Text strong>{order.displayOrderNumber || order.orderNumber}</Typography.Text>
        {order.customerName && <Typography.Text type="secondary">{order.customerName}</Typography.Text>}
      </div>
      <div className="transport-order-points__fields">
        {([['loadingAddress', loading], ['deliveryAddress', delivery]] as const).map(([field, addresses]) =>
          <Form.Item key={field} className={`transport-order-points__${field}`} name={['orderRoutePointsByOrder', order.id, field]}
            label={<span className="transport-order-points__label"><span className={`transport-order-points__pin transport-order-points__pin--${field === 'loadingAddress' ? 'a' : 'b'}`}>{field === 'loadingAddress' ? 'A' : 'B'}</span>{t(`orderRoutePoints.${field}`)}</span>}
            rules={[{ validator: (_, value) => addresses.length === 0 || addresses.includes(value) ? Promise.resolve() : Promise.reject(new Error(t('orderRoutePoints.required'))) }]}>
            <Select aria-label={`${order.displayOrderNumber || order.orderNumber}: ${t(`orderRoutePoints.${field}`)}`}
              placeholder={t('orderRoutePoints.choose')} disabled={!addresses.length}
              options={addresses.map(address => ({ value: address, label: address.replace(/\n/g, ', ') }))} />
          </Form.Item>)}
        <ArrowRightOutlined className="transport-order-points__arrow" aria-hidden="true" />
      </div>
    </div>)}
  </section>;
}
